import { Button } from "@/components/ui/button";
import { adhdAppImage } from "@/assets";

export default function Services() {
  return (
    <div className="flex flex-col pt-10">
      {/* Page Hero */}
      <section className="pt-20 pb-24 bg-background">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center space-y-8">
          <span className="text-sm font-semibold uppercase tracking-widest text-primary block">
            My Services
          </span>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif text-foreground leading-[1.1] tracking-tight text-balance mx-auto">
            Disciplined execution. Strategic clarity.
          </h1>
          <p className="text-xl text-muted-foreground leading-relaxed max-w-2xl mx-auto pt-4 text-balance">
            I offer a focused set of disciplines designed to help you understand your data, communicate your value, and build tools that work.
          </p>
          <p className="text-base text-primary font-serif italic pt-2">
            "Emerging analyst building through real projects, disciplined study, and applied work."
          </p>
        </div>
      </section>

      {/* 01: Data Analysis */}
      <section className="py-24 border-t border-border/40">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            <div className="lg:col-span-3">
              <div className="text-5xl font-serif text-primary/40 mb-4">01</div>
              <h2 className="text-3xl font-serif text-foreground">Data Analysis</h2>
            </div>
            <div className="lg:col-span-5 space-y-6 text-lg text-muted-foreground leading-relaxed">
              <p>
                A data-driven mindset is the foundation of effective strategy. I approach data analysis with rigorous learning and a focus on real-world projects, turning raw information into actionable insights.
              </p>
              <p>
                From cleaning messy datasets to building predictive models, my goal is always clarity. Data shouldn't just be collected; it should be understood and acted upon.
              </p>
            </div>
            <div className="lg:col-span-4">
              <div className="bg-plum p-8 text-white rounded-sm">
                <h3 className="text-sm uppercase tracking-widest font-semibold text-white/60 mb-6 border-b border-white/20 pb-4">
                  Current Focus Areas
                </h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <span className="text-secondary mt-1">✦</span>
                    <span className="font-serif text-lg tracking-wide">Statistical Foundations</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-secondary mt-1">✦</span>
                    <span className="font-serif text-lg tracking-wide">Predictive Modeling</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-secondary mt-1">✦</span>
                    <span className="font-serif text-lg tracking-wide">Data Visualization</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="text-secondary mt-1">✦</span>
                    <span className="font-serif text-lg tracking-wide">Actionable Reporting</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 02: Conversion-Focused Website Design */}
      <section className="py-32 bg-accent/20 border-t border-border/40">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
          <div className="text-center mb-20">
            <div className="text-5xl font-serif text-primary/40 mb-4">02</div>
            <h2 className="text-4xl md:text-5xl font-serif text-foreground">Conversion-Focused<br/>Website Design</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center md:text-left">
            <div className="space-y-4">
              <h3 className="text-2xl font-serif text-foreground">Trust & Clarity</h3>
              <p className="text-muted-foreground leading-relaxed">
                A successful website is built on a foundation of trust. Clear navigation, professional aesthetics, and transparent communication create environments where users feel confident taking action.
              </p>
            </div>
            <div className="space-y-4">
              <h3 className="text-2xl font-serif text-foreground">Strategic Messaging</h3>
              <p className="text-muted-foreground leading-relaxed">
                Design without message is just decoration. Every visual choice supports a core narrative, guiding the visitor's eye exactly where it needs to go to understand your value proposition.
              </p>
            </div>
            <div className="space-y-4">
              <h3 className="text-2xl font-serif text-foreground">Action-Oriented</h3>
              <p className="text-muted-foreground leading-relaxed">
                Every choice is optimized for conversion. From button placement to color psychology, the design works hard to turn passive visitors into engaged leads.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 03: App Development */}
      <section className="bg-background border-t border-border/40">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          <div className="relative h-[60vh] lg:h-auto overflow-hidden">
            <img 
              src={adhdAppImage}
              alt="App Development — Neurodivergence Accountability App" 
              className="object-cover w-full h-full object-center"
            />
          </div>
          <div className="flex items-center p-12 lg:p-24 xl:px-32">
            <div className="max-w-xl space-y-8">
              <div className="text-5xl font-serif text-primary/40 mb-2">03</div>
              <h2 className="text-4xl md:text-5xl font-serif text-foreground">App Development</h2>
              <div className="space-y-6 text-lg text-muted-foreground leading-relaxed">
                <p>
                  Building functional, reliable applications that solve specific problems. I focus on creating digital tools that are intuitive to use and robust beneath the surface.
                </p>
                <p>
                  My approach prioritizes human-centered architecture — ensuring that the technology serves the user, rather than forcing the user to adapt to the technology.
                </p>
              </div>
              <a
                href="https://calendly.com/shateara-davis-proton"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm uppercase tracking-wider font-semibold text-primary hover:text-primary/80 transition-colors group pt-4"
              >
                Discuss a project 
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-plum text-white py-24 border-t-4 border-primary">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl text-center space-y-8">
          <h2 className="text-4xl md:text-5xl font-serif text-white leading-tight">
            Let's build something intentional.
          </h2>
          <p className="text-lg md:text-xl text-white/80 leading-relaxed font-light">
            Ready to apply rigorous analysis, thoughtful design, or robust development to your next project?
          </p>
          <div className="pt-8">
            <a href="https://calendly.com/shateara-davis-proton" target="_blank" rel="noopener noreferrer">
              <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-white rounded-none px-10 py-7 text-sm uppercase tracking-wider font-semibold">
                Book a Consultation
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
